import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, FileArrowDown, Phone } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Products() {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products?active_only=true`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const featuredProducts = products.filter(p => p.product_type === 'featured');
  const xgridsProducts = products.filter(p => p.product_type === 'xgrids');
  const supportingProducts = products.filter(p => p.product_type === 'supporting');

  const ProductCard = ({ product, index }) => (
    <motion.div
      key={product.id}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group card-hover bg-xgrids-bg-surface rounded-sm overflow-hidden"
      data-testid={`product-card-${product.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="aspect-square bg-black/50 overflow-hidden flex items-center justify-center p-8">
        <img
          src={product.image_url}
          alt={product.name}
          className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-mono text-xgrids-brand-blue text-xs tracking-widest uppercase">
            {product.category}
          </span>
        </div>
        <h3 className="font-heading text-xl font-bold text-white mb-3">{product.name}</h3>
        <p className="text-xgrids-text-secondary text-sm mb-4 line-clamp-3">{product.description}</p>
        
        {/* Specs */}
        {product.specs && (
          <div className="flex flex-wrap gap-2 mb-4">
            {product.specs.split('|').slice(0, 3).map((spec, i) => (
              <span 
                key={i}
                className="inline-flex items-center gap-1 text-xs text-xgrids-text-muted"
              >
                <Check size={12} className="text-xgrids-brand-primary" />
                {spec.trim()}
              </span>
            ))}
          </div>
        )}

        {/* Price - Always Contact Us */}
        <div className="mb-4">
          <p className="font-heading text-xl font-bold text-xgrids-brand-primary flex items-center gap-2">
            <Phone size={20} weight="fill" />
            {t('products.contactUs')}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          {product.brochure_url && (
            <a
              href={product.brochure_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-xgrids-text-secondary hover:text-xgrids-brand-primary transition-colors"
              data-testid={`brochure-${product.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <FileArrowDown size={16} />
              {t('products.brochure')}
            </a>
          )}
          <a
            href="/tentang#contact"
            className="inline-flex items-center gap-2 text-sm text-xgrids-brand-primary hover:text-xgrids-brand-hover transition-colors"
          >
            {t('products.requestQuote')}
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen pt-20" data-testid="products-page">
      {/* Header */}
      <section className="py-20 lg:py-28 bg-xgrids-bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="font-mono text-xgrids-brand-primary text-sm tracking-widest uppercase mb-4">{t('products.catalog')}</p>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold text-white mb-6" data-testid="products-title">
              {t('products.title')}
            </h1>
            <p className="text-xgrids-text-secondary text-lg max-w-2xl">
              {t('products.desc')}
            </p>
          </motion.div>
        </div>
      </section>

      {loading ? (
        <div className="text-center py-20">
          <div className="animate-pulse text-xgrids-text-secondary">{t('common.loading')}</div>
        </div>
      ) : (
        <>
          {/* Featured Products */}
          {featuredProducts.length > 0 && (
            <section className="py-20 lg:py-24" data-testid="featured-products-section">
              <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mb-12"
                >
                  <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-2">
                    {t('products.featured')}
                  </h2>
                  <p className="text-xgrids-text-secondary">
                    {t('products.featuredDesc')}
                  </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {featuredProducts.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.2 }}
                      className="card-hover bg-xgrids-bg-surface rounded-sm overflow-hidden"
                      data-testid={`featured-product-${index}`}
                    >
                      <div className="aspect-video bg-black/50 overflow-hidden flex items-center justify-center p-12">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-8">
                        <span className="font-mono text-xgrids-brand-blue text-xs tracking-widest uppercase">
                          {product.category}
                        </span>
                        <h3 className="font-heading text-2xl lg:text-3xl font-bold text-white mt-2 mb-4">
                          {product.name}
                        </h3>
                        <p className="text-xgrids-text-secondary mb-6 leading-relaxed">
                          {product.description}
                        </p>

                        {/* Specs */}
                        {product.specs && (
                          <div className="flex flex-wrap gap-3 mb-6">
                            {product.specs.split('|').map((spec, i) => (
                              <span 
                                key={i}
                                className="inline-flex items-center gap-2 bg-xgrids-bg-primary px-3 py-1.5 text-sm text-xgrids-text-secondary border border-white/10"
                              >
                                <Check size={14} className="text-xgrids-brand-primary" />
                                {spec.trim()}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Price & Actions */}
                        <div className="flex items-center justify-between flex-wrap gap-4 pt-4 border-t border-white/10">
                          <div>
                            <p className="text-xgrids-text-muted text-sm mb-1">{t('products.priceFrom')}</p>
                            <p className="font-heading text-2xl font-bold text-xgrids-brand-primary flex items-center gap-2">
                              <Phone size={24} weight="fill" />
                              {t('products.contactUs')}
                            </p>
                          </div>
                          <div className="flex gap-3">
                            {product.brochure_url && (
                              <a
                                href={product.brochure_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-outline rounded-none inline-flex items-center gap-2 text-sm"
                                data-testid={`brochure-btn-${index}`}
                              >
                                <FileArrowDown size={18} />
                                {t('products.brochure')}
                              </a>
                            )}
                            <a
                              href="/tentang#contact"
                              className="btn-primary rounded-none inline-flex items-center gap-2 text-sm"
                              data-testid={`inquiry-btn-${index}`}
                            >
                              {t('products.requestQuote')}
                              <ArrowRight size={18} />
                            </a>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* All XGRIDS Products */}
          {xgridsProducts.length > 0 && (
            <section className="py-20 lg:py-24 bg-xgrids-bg-secondary" data-testid="xgrids-products-section">
              <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mb-12"
                >
                  <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-2">
                    {t('products.allXgrids')}
                  </h2>
                  <p className="text-xgrids-text-secondary">
                    {t('products.allXgridsDesc')}
                  </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {xgridsProducts.map((product, index) => (
                    <ProductCard key={product.id} product={product} index={index} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Supporting Products */}
          {supportingProducts.length > 0 && (
            <section className="py-20 lg:py-24" data-testid="supporting-products-section">
              <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mb-12"
                >
                  <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-2">
                    {t('products.supporting')}
                  </h2>
                  <p className="text-xgrids-text-secondary">
                    {t('products.supportingDesc')}
                  </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {supportingProducts.map((product, index) => (
                    <ProductCard key={product.id} product={product} index={index} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-xgrids-bg-secondary">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-6">
              {t('products.needHelp')}
            </h2>
            <p className="text-xgrids-text-secondary mb-8">
              {t('products.needHelpDesc')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://xgrids.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline rounded-none"
                data-testid="xgrids-global-btn"
              >
                {t('products.visitXgrids')}
              </a>
              <a
                href="/tentang#contact"
                className="btn-primary rounded-none inline-flex items-center gap-2"
              >
                {t('nav.contactUs')}
                <ArrowRight size={18} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
